import {
  ChangeDetectionStrategy,
  Component,
  signal,
  viewChild,
  TemplateRef,
  ViewContainerRef,
  inject,
  computed,
  ElementRef,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import { NgIcon, provideIcons } from '@ng-icons/core';
// T2 ([[TASK-049]]): only the icons this app's own templates/nav config use and
// the library does not already register are kept here. `lucideLayers` is used
// directly by `app.html`; the rest are nav-config icon names resolved through
// `tf-sidebar-item` (which registers its own common set). See BUNDLE-001 §4a.
import {
  lucideTrendingUp,
  lucideDatabase,
  lucideLayers,
  lucideWarehouse,
  lucidePackage,
  lucideTruck,
  lucideMapPin,
  lucideGlobe,
  lucideLink2,
  lucideCreditCard,
  lucideCalculator,
  lucideBookmark,
} from '@ng-icons/lucide';

import { ZardSheetService, ZardToastComponent } from '@trueform/ui/ui';
import { AppAuthService, ThemeService, TranslatePipe, TRUEFORM_BRAND } from '@trueform/ui/core';

import { CompanySwitcherComponent } from '@/company-switcher/company-switcher.component';
import { PUBLIC_ROUTE_PATHS } from '@/core/config/public-routes';
import { PwaUpdateService } from '@/core/pwa/pwa-update.service';

import {
  type AppNotification,
  FooterComponent,
  HeaderComponent,
  NavigationService,
  SidebarComponent,
  SidebarItemComponent,
} from '@trueform/ui/layout';

@Component({
  selector: 'app-root',
  host: {
    '(document:click)': 'onDocumentClick($event)',
  },
  imports: [
    RouterOutlet,
    NgIcon,
    ZardToastComponent,
    TranslatePipe,
    HeaderComponent,
    FooterComponent,
    SidebarComponent,
    SidebarItemComponent,
    CompanySwitcherComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideIcons({
      lucideTrendingUp,
      lucideDatabase,
      lucideLayers,
      lucideWarehouse,
      lucidePackage,
      lucideTruck,
      lucideMapPin,
      lucideGlobe,
      lucideLink2,
      lucideCreditCard,
      lucideCalculator,
      lucideBookmark,
    }),
  ],
})
export class App {
  private readonly router = inject(Router);
  private readonly sheetService = inject(ZardSheetService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly elementRef = inject(ElementRef);
  protected readonly brand = inject(TRUEFORM_BRAND);
  protected readonly authService = inject(AppAuthService);
  protected readonly navService = inject(NavigationService);
  private readonly themeService = inject(ThemeService);
  private readonly pwaUpdateService = inject(PwaUpdateService);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  protected readonly isPublicRoute = computed(() => {
    const path = this.currentUrl().split('?')[0];
    return PUBLIC_ROUTE_PATHS.some((r) => path === r || path.startsWith(r + '/'));
  });

  onDocumentClick(event: MouseEvent) {
    const wrapper = this.elementRef.nativeElement.querySelector('[data-notifications-wrapper]');
    if (this.isNotificationsOpen() && wrapper && !wrapper.contains(event.target as Node)) {
      this.isNotificationsOpen.set(false);
    }
    const profileWrapper = this.elementRef.nativeElement.querySelector('[data-profile-wrapper]');
    if (this.isProfileOpen() && profileWrapper && !profileWrapper.contains(event.target as Node)) {
      this.isProfileOpen.set(false);
    }
  }

  /** Resolved colour scheme, owned by ThemeService (persisted + system-aware). */
  readonly isDarkMode = this.themeService.isDark;

  readonly mobileMenuTemplate = viewChild.required<TemplateRef<unknown>>('mobileMenuTemplate');

  readonly userEmail = computed(() => {
    const user = this.authService.currentUser();
    return user?.email || 'User';
  });

  readonly userName = computed(() => {
    const user = this.authService.currentUser();
    const firstName = user?.first_name || '';
    const lastName = user?.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || this.userEmail();
  });

  readonly userInitials = computed(() => {
    const user = this.authService.currentUser();
    const firstName = user?.first_name || '';
    const lastName = user?.last_name || '';
    if (firstName || lastName) {
      return ((firstName[0] || '') + (lastName[0] || '')).toUpperCase();
    }
    const email = this.userEmail();
    return email ? email.slice(0, 2).toUpperCase() : 'US';
  });

  readonly userPhoto = computed(() => {
    const user = this.authService.currentUser();
    return user?.profile_picture_url || '';
  });

  readonly isNotificationsOpen = signal(false);
  readonly notifications = signal<AppNotification[]>([
    {
      id: '1',
      title: 'Campaign Performance Boost',
      description: 'Your ROI for "Social Amplification" has increased by 12.4% over the last hour.',
      timestamp: '5m ago',
      read: false,
      type: 'success',
    },
    {
      id: '2',
      title: 'New Comment on Analytics',
      description: 'John Doe commented: "We need to expand search metrics to cover TikTok ads."',
      timestamp: '1h ago',
      read: false,
      type: 'comment',
    },
    {
      id: '3',
      title: 'Ad Account Warning',
      description:
        'Google Ads integration token is about to expire. Re-authenticate to prevent tracking disruption.',
      timestamp: '3h ago',
      read: true,
      type: 'warning',
    },
    {
      id: '4',
      title: 'System Security Audit',
      description: 'Authentication middleware session rules successfully synchronized and active.',
      timestamp: '1d ago',
      read: true,
      type: 'info',
    },
  ]);

  readonly unreadNotificationsCount = computed(
    () => this.notifications().filter((n) => !n.read).length,
  );

  readonly isProfileOpen = signal(false);

  markNotificationAsRead(id: string) {
    this.notifications.update((notifs) =>
      notifs.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
  }

  markAllNotificationsAsRead() {
    this.notifications.update((notifs) => notifs.map((n) => ({ ...n, read: true })));
  }

  dismissNotification(id: string) {
    this.notifications.update((notifs) => notifs.filter((n) => n.id !== id));
  }

  clearAllNotifications() {
    this.notifications.set([]);
  }

  constructor() {
    this.pwaUpdateService.init();

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.isProfileOpen.set(false);
        this.isNotificationsOpen.set(false);
      });
  }

  goToProfileSettings() {
    this.isProfileOpen.set(false);
    void this.router.navigate(['/profile']);
  }

  toggleDarkMode() {
    this.themeService.toggle();
  }

  openMobileDrawer() {
    this.sheetService.create({
      zTitle: `${this.brand.name} Menu`,
      zDescription: 'Quick navigation links',
      zContent: this.mobileMenuTemplate(),
      zSide: 'left',
      zHideFooter: true,
      zViewContainerRef: this.viewContainerRef,
    });
  }

  async handleLogout(): Promise<void> {
    this.isProfileOpen.set(false);
    this.isNotificationsOpen.set(false);
    try {
      await this.authService.logout();
    } catch {
      // Logout is best-effort: the local session is already torn down by
      // authService.logout()'s own cleanup regardless of outcome.
    } finally {
      void this.router.navigate(['/login'], { replaceUrl: true });
    }
  }
}
